import React from 'react';
import { Check, X, Loader2, Minus, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Compact, shadcn/Tailwind status cell used by the enriched funerals board.
 * Shared display primitive for the DEC / Benefits / Registry / Attestation
 * editors (they render this as their trigger when `compact`, keeping their
 * existing edit dialogs). Colour encodes meaning:
 *   done    — complete (green fill, check)
 *   ready   — all fields present, not yet marked (green outline)
 *   missing — required fields missing (red)
 *   warn    — not applicable / ineligible (amber)
 *   na      — not needed yet / off (grey)
 *   turn    — waiting on us to act, e.g. SIED form ready for our signature (blue, pen)
 *   none    — nothing to show / does not apply (grey, dash)
 */
export type StatusCellVariant = 'done' | 'ready' | 'missing' | 'warn' | 'na' | 'turn' | 'none';

const VARIANTS: Record<StatusCellVariant, string> = {
  done: 'border-transparent bg-green-600 text-white hover:bg-green-700',
  ready: 'border-green-500 text-green-600 bg-transparent hover:bg-green-50 hover:border-green-600',
  missing: 'border-red-400 text-red-500 bg-red-50 hover:bg-red-100 hover:border-red-500',
  warn: 'border-transparent bg-orange-500 text-white hover:bg-orange-600',
  na: 'border-gray-300 text-gray-400 bg-transparent hover:bg-gray-100 hover:border-gray-400',
  turn: 'border-blue-500 text-blue-600 bg-blue-50 hover:bg-blue-100 hover:border-blue-600',
  none: 'border-gray-300 text-gray-400 bg-transparent hover:bg-gray-100 hover:border-gray-400',
};

export const StatusCell = ({
  variant,
  onClick,
  title,
  loading,
}: {
  variant: StatusCellVariant;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  loading?: boolean;
}) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={cn(
      'inline-flex h-6 w-6 items-center justify-center rounded-md border transition-all cursor-pointer hover:shadow-sm',
      loading ? VARIANTS.na : VARIANTS[variant],
    )}
  >
    {loading ? (
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
    ) : variant === 'done' ? (
      <Check className="h-3.5 w-3.5" strokeWidth={3} />
    ) : variant === 'warn' ? (
      <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
    ) : variant === 'turn' ? (
      <PenLine className="h-3.5 w-3.5" strokeWidth={2.25} />
    ) : variant === 'none' ? (
      <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
    ) : (
      <X className="h-3.5 w-3.5" strokeWidth={2.5} />
    )}
  </button>
);
